import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useI18n } from '../../contexts/I18nContext';

/**
 * In-app replacement for `window.confirm` / `window.prompt`.
 *
 * The native dialogs are drawn by the browser chrome: they carry the origin
 * ("app.firmio.cleancodeculture.com says"), ignore the app's theme entirely and
 * block the main thread. This provider exposes the same imperative shape as a
 * promise, so a call site changes from
 *
 *   if (!window.confirm(msg)) return;
 * to
 *   if (!(await confirm(msg))) return;
 *
 * and keeps reading top-to-bottom instead of being inverted into callbacks.
 *
 * Only one dialog can be open at a time, which matches the native behaviour the
 * call sites were written against.
 */

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
}

export interface PromptOptions extends ConfirmOptions {
  placeholder?: string;
  defaultValue?: string;
}

/** `confirm('Delete?')` is shorthand for `confirm({ message: 'Delete?' })`. */
type ConfirmArg = string | ConfirmOptions;
type PromptArg = string | PromptOptions;

interface ConfirmContextValue {
  confirm: (arg: ConfirmArg) => Promise<boolean>;
  prompt: (arg: PromptArg) => Promise<string | null>;
}

const ConfirmContext = createContext<ConfirmContextValue | undefined>(undefined);

type Pending =
  | { kind: 'confirm'; opts: ConfirmOptions; resolve: (value: boolean) => void }
  | { kind: 'prompt'; opts: PromptOptions; resolve: (value: string | null) => void };

function normalize<T extends ConfirmOptions>(arg: string | T): T {
  return (typeof arg === 'string' ? { message: arg } : arg) as T;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);

  // Settling always clears the dialog, so a rejected promise can never strand
  // an invisible pending request behind a closed modal.
  const settle = useCallback((value: boolean | string | null) => {
    setPending((current) => {
      if (current) {
        if (current.kind === 'confirm') current.resolve(value === true);
        else current.resolve(typeof value === 'string' ? value : null);
      }
      return null;
    });
  }, []);

  const value = useMemo<ConfirmContextValue>(
    () => ({
      confirm: (arg) =>
        new Promise<boolean>((resolve) => {
          setPending({ kind: 'confirm', opts: normalize(arg), resolve });
        }),
      prompt: (arg) =>
        new Promise<string | null>((resolve) => {
          setPending({ kind: 'prompt', opts: normalize<PromptOptions>(arg), resolve });
        }),
    }),
    [],
  );

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {pending && <ConfirmModal pending={pending} settle={settle} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmModal({
  pending,
  settle,
}: {
  pending: Pending;
  settle: (value: boolean | string | null) => void;
}) {
  const { t } = useI18n();
  const { opts } = pending;
  const isPrompt = pending.kind === 'prompt';
  const [text, setText] = useState(isPrompt ? ((opts as PromptOptions).defaultValue ?? '') : '');
  const inputRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  const cancel = () => settle(isPrompt ? null : false);

  // Focus the field the user is expected to act on, so Enter/Escape work
  // immediately without a click first — as they do in the native dialog.
  useEffect(() => {
    if (isPrompt) inputRef.current?.select();
    else confirmRef.current?.focus();
  }, [isPrompt]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        cancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={opts.title ?? opts.message}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cancel();
      }}
    >
      {/* A form so Enter submits from the prompt field, like the native dialog. */}
      <form
        className="ui-modal-panel ui-modal-compact w-full max-w-md"
        onSubmit={(e) => {
          e.preventDefault();
          settle(isPrompt ? text : true);
        }}
      >
        {opts.title && (
          <h2 className="font-heading text-base font-semibold text-foreground">{opts.title}</h2>
        )}
        <p className={`text-sm text-foreground ${opts.title ? 'mt-2' : ''}`}>{opts.message}</p>

        {isPrompt && (
          <input
            ref={inputRef}
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={(opts as PromptOptions).placeholder}
            className="ui-input mt-3"
            autoFocus
          />
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={cancel} className="ui-btn-secondary">
            {opts.cancelLabel ?? t('common.cancel')}
          </button>
          <button
            ref={confirmRef}
            type="submit"
            className={opts.danger ? 'ui-btn-danger' : 'ui-btn-primary'}
          >
            {opts.confirmLabel ?? t('common.confirm')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Promise-based `window.confirm` replacement. Resolves false on cancel. */
export function useConfirm(): ConfirmContextValue['confirm'] {
  return useConfirmContext().confirm;
}

/** Promise-based `window.prompt` replacement. Resolves null on cancel. */
export function usePrompt(): ConfirmContextValue['prompt'] {
  return useConfirmContext().prompt;
}

function useConfirmContext(): ConfirmContextValue {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm/usePrompt must be used within a ConfirmProvider');
  return ctx;
}
